import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  books,
  comparisonDimensions,
  BookData,
} from "../../../data/bookComparison";
import {
  BookOpen,
  Github,
  ExternalLink,
  Info,
  ShoppingCart,
  ArrowUpDown,
  Star,
} from "lucide-react";
import { Tooltip, Tag, Modal } from "@multiagentbook/ui";

type SortOption = "average" | "code-files" | "agent-concepts" | "multi-agent-patterns" | "hands-on-implementation" | "ux-principles";

/**
 * Affiliate links must be marked rel="sponsored" — Google treats an
 * undisclosed paid link as a link scheme, which on a page whose purpose is to
 * rank works directly against it. Only the amzn.to links are paid; the plain
 * Amazon URL is the author's own book, not a commission.
 */
const amazonRel = (url: string) =>
  /amzn\.to|[?&]tag=/.test(url)
    ? "sponsored nofollow noopener noreferrer"
    : "noopener noreferrer";

const BookComparisonLab: React.FC = () => {
  const { t } = useTranslation("aiAgentBooks");

  const sortOptions: { value: SortOption; label: string }[] = [
    { value: "average", label: t("sortOptions.average") },
    { value: "code-files", label: t("sortOptions.codeFiles") },
    { value: "agent-concepts", label: t("sortOptions.agentConcepts") },
    { value: "multi-agent-patterns", label: t("sortOptions.multiAgentPatterns") },
    { value: "hands-on-implementation", label: t("sortOptions.handsOnImplementation") },
    { value: "ux-principles", label: t("sortOptions.uxPrinciples") },
  ];
  const [sortBy, setSortBy] = useState<SortOption>("average");
  const [showScores, setShowScores] = useState(false);
  const [selectedBook, setSelectedBook] = useState<BookData | null>(null);

  // Render star rating (subtle yellow stars)
  const renderStars = (score: number) => {
    return (
      <div className="flex items-center gap-0.5 mb-1">
        {[1, 2, 3, 4, 5].map((i) => (
          <Star
            key={i}
            className={`w-3 h-3 ${
              i <= score
                ? "fill-amber-400 text-amber-400"
                : "fill-transparent text-secondary/30"
            }`}
          />
        ))}
      </div>
    );
  };

  // Calculate average score for a book
  const getAverageScore = (book: BookData): number => {
    const scores = book.dimensions.map((d) => d.score);
    return scores.reduce((a, b) => a + b, 0) / scores.length;
  };

  // Get score for a specific dimension
  const getDimensionScore = (book: BookData, slug: string): number => {
    const dim = book.dimensions.find((d) => d.slug === slug);
    return dim?.score ?? 0;
  };

  // Sort books based on selected option
  const sortedBooks = useMemo(() => {
    return [...books].sort((a, b) => {
      if (sortBy === "average") {
        return getAverageScore(b) - getAverageScore(a);
      }
      if (sortBy === "code-files") {
        return b.metrics.totalCodeFiles - a.metrics.totalCodeFiles;
      }
      return getDimensionScore(b, sortBy) - getDimensionScore(a, sortBy);
    });
  }, [sortBy]);

  // Render a cell with optional stars and justification text
  const renderTableCell = (score: number, justification: string) => {
    return (
      <div className="min-w-[160px] max-w-[220px]">
        {showScores && renderStars(score)}
        <div className="text-sm leading-relaxed text-secondary line-clamp-5">
          {justification}
        </div>
      </div>
    );
  };

  // Render comparison table
  const renderComparisonTable = () => (
    <div className="overflow-x-auto mb-12">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-secondary/20">
            <th className="text-left p-3 font-semibold text-primary">{t("comparison.book")}</th>
            {comparisonDimensions.map((dim) => (
              <th key={dim.slug} className="text-left p-3 font-semibold text-primary">
                <Tooltip title={dim.description}>
                  <span className="text-sm cursor-help">{dim.name}</span>
                </Tooltip>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedBooks.map((book) => (
            <tr
              key={book.id}
              className={`border-b border-secondary/10 hover:bg-secondary/5 ${
                book.isAuthorBook ? "bg-accent/5" : ""
              }`}
            >
              <td className="p-3 align-top">
                <div className="flex items-start gap-3">
                  <button
                    onClick={() => setSelectedBook(book)}
                    className="w-28 h-36 bg-secondary/10 rounded overflow-hidden flex-shrink-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-accent/50 transition-transform duration-200 hover:scale-[1.03] hover:shadow-md"
                    aria-label={t("bookDetails.viewDetails", { title: book.title })}
                  >
                    <img
                      src={book.coverImage}
                      alt={book.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          "/images/book-placeholder.png";
                      }}
                    />
                  </button>
                  <div>
                    <button
                      onClick={() => setSelectedBook(book)}
                      className="font-medium text-primary text-sm hover:text-accent text-left"
                    >
                      {book.title}
                    </button>
                    <div className="text-xs text-secondary">{book.author}</div>
                    <a
                      href={book.githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-secondary/70 mt-1 hover:text-accent block"
                    >
                      {book.metrics.totalCodeFiles} {t("comparison.codeFiles")}
                    </a>
                  </div>
                </div>
              </td>
              {comparisonDimensions.map((dim) => {
                const bookDim = book.dimensions.find((d) => d.slug === dim.slug);
                return (
                  <td key={dim.slug} className="p-3 align-top">
                    {bookDim && renderTableCell(bookDim.score, bookDim.justification)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Render detailed book section
  const renderBookSection = (book: BookData) => {
    return (
      <section
        id={book.id}
        key={book.id}
        className={`border rounded-lg overflow-hidden mb-6 scroll-mt-4 ${
          book.isAuthorBook ? "border-accent/30" : "border-secondary/20"
        }`}
      >
        {/* Book Header */}
        <div className="p-6 bg-secondary/5">
          <div className="flex gap-6">
            {/* Book Cover */}
            <div className="w-24 h-32 bg-secondary/10 rounded overflow-hidden flex-shrink-0 shadow-md">
              <img
                src={book.coverImage}
                alt={book.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src =
                    "/images/book-placeholder.png";
                }}
              />
            </div>

            {/* Book Info */}
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-primary">
                {book.title}
              </h2>
              <p className="text-sm text-secondary mt-1">
                {book.author} • {book.year}
              </p>

              <p className="text-sm text-secondary mt-3">
                {book.description}
              </p>

              {/* Quick Links */}
              <div className="flex items-center gap-4 mt-4">
                <a
                  href={book.githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-sm text-accent hover:text-accent/80"
                >
                  <Github className="w-4 h-4" />
                  {t("bookDetails.codeRepository")}
                </a>
                <span className="text-sm text-secondary">
                  {book.metrics.totalCodeFiles} {t("bookDetails.pythonNotebookFiles")}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Dimension Scores - Simple table */}
        <div className="p-6 border-t border-secondary/10">
          <h3 className="font-medium text-primary mb-3">{t("bookDetails.coverageByDimension")}</h3>
          <table className="w-full text-sm">
            <tbody>
              {book.dimensions.map((dim) => (
                <tr key={dim.slug} className="border-b border-secondary/10 last:border-0">
                  <td className="py-2 pr-4 font-medium text-primary w-40">
                    <div>{dim.name}</div>
                    {showScores && renderStars(dim.score)}
                  </td>
                  <td className="py-2 text-secondary">{dim.justification}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Topics & Focus */}
        <div className="p-6 border-t border-secondary/10 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Topics */}
          <div>
            <h4 className="font-medium text-primary mb-2">{t("bookDetails.topicsCovered")}</h4>
            <div className="flex flex-wrap gap-1">
              {book.topics.map((topic, i) => (
                <Tag key={i} className="text-xs">
                  {topic}
                </Tag>
              ))}
            </div>
          </div>

          {/* Focus */}
          <div>
            <h4 className="font-medium text-primary mb-2">{t("bookDetails.whatBookProvides")}</h4>
            <ul className="text-sm text-secondary space-y-1">
              {book.focus.map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-accent mt-1">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Purchase Links */}
        <div className="p-6 border-t border-secondary/10 bg-secondary/5">
          <div className="flex flex-wrap gap-2">
            {book.amazonUrl && (
              <a
                href={book.amazonUrl}
                target="_blank"
                rel={amazonRel(book.amazonUrl)}
                className="flex items-center gap-1 px-3 py-1.5 rounded text-sm bg-accent text-white hover:text-white hover:opacity-90 transition-opacity"
              >
                <ShoppingCart className="w-4 h-4" />
                Amazon
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
            <a
              href={book.githubUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 px-3 py-1.5 rounded text-sm bg-white dark:bg-slate-700 text-primary hover:bg-secondary/10 border border-secondary/20"
            >
              <Github className="w-4 h-4" />
              GitHub
            </a>
          </div>
        </div>
      </section>
    );
  };

  return (
    <div>
      {/* Book Details Modal */}
      <Modal
        isOpen={!!selectedBook}
        onClose={() => setSelectedBook(null)}
        title={selectedBook?.title || t("bookDetails.title")}
        size="xl"
      >
        {selectedBook && (
          <div className="p-6">
            {/* Header with Cover and Info */}
            <div className="flex flex-col md:flex-row gap-6 mb-6">
              {/* Book Cover */}
              <div className="flex-shrink-0 mx-auto md:mx-0">
                <div className="w-40 h-52 bg-secondary/10 rounded overflow-hidden shadow">
                  <img
                    src={selectedBook.coverImage}
                    alt={selectedBook.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = "/images/book-placeholder.png";
                    }}
                  />
                </div>
              </div>

              {/* Book Info */}
              <div className="flex-1 min-w-0">
                <p className="text-secondary mb-2">
                  {selectedBook.author} • {selectedBook.year}
                </p>
                <p className="text-sm text-secondary leading-relaxed mb-4">
                  {selectedBook.description}
                </p>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {selectedBook.amazonUrl && (
                    <a
                      href={selectedBook.amazonUrl}
                      target="_blank"
                      rel={amazonRel(selectedBook.amazonUrl)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium bg-accent text-white hover:text-white hover:opacity-90 transition-opacity"
                    >
                      <ShoppingCart className="w-4 h-4" />
                      {t("bookDetails.buyOnAmazon")}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  <a
                    href={selectedBook.githubUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium bg-slate-800 dark:bg-slate-700 text-white hover:text-white hover:opacity-90 transition-opacity"
                  >
                    <Github className="w-4 h-4" />
                    {t("bookDetails.viewCode")}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  {selectedBook.officialUrl && (
                    <a
                      href={selectedBook.officialUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium border border-secondary/30 text-primary hover:bg-secondary/10 transition-colors"
                    >
                      <BookOpen className="w-4 h-4" />
                      {t("bookDetails.officialSite")}
                    </a>
                  )}
                </div>

                {/* Quick Stats */}
                <div className="flex flex-wrap gap-3 text-sm">
                  <span className="px-2 py-1 bg-secondary/10 rounded text-secondary">
                    {selectedBook.metrics.totalCodeFiles} {t("comparison.codeFiles")}
                  </span>
                  <span className="px-2 py-1 bg-secondary/10 rounded text-secondary">
                    {t("comparison.pythonNotebooks", { python: selectedBook.metrics.pythonFiles, notebooks: selectedBook.metrics.notebookFiles })}
                  </span>
                </div>
              </div>
            </div>

            {/* Dimension Scores */}
            <div className="border-t border-secondary/10 pt-6 mb-6">
              <h3 className="font-semibold text-primary mb-3 flex items-center gap-2">
                <Info className="w-4 h-4" />
                {t("bookDetails.coverageByDimension")}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {selectedBook.dimensions.map((dim) => (
                  <div
                    key={dim.slug}
                    className="p-3 rounded bg-secondary/5 border border-secondary/10"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-primary text-sm">{dim.name}</span>
                      {showScores && renderStars(dim.score)}
                    </div>
                    <p className="text-sm text-secondary leading-relaxed">
                      {dim.justification}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Topics & Focus */}
            <div className="border-t border-secondary/10 pt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-primary mb-2 text-sm">{t("bookDetails.topicsCovered")}</h4>
                <div className="flex flex-wrap gap-1.5">
                  {selectedBook.topics.map((topic, i) => (
                    <Tag key={i} className="text-xs">
                      {topic}
                    </Tag>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-primary mb-2 text-sm">{t("bookDetails.whatBookProvides")}</h4>
                <ul className="text-sm text-secondary space-y-1">
                  {selectedBook.focus.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-accent mt-0.5">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Disclosure - At top */}
      <div className="mb-6 p-4 bg-secondary/5 border border-secondary/20 rounded-lg">
        <p className="text-sm text-secondary">
          <strong>{t("disclosure.title")}</strong> {t("disclosure.text")}
        </p>
      </div>

      {/* Header with Sort and Toggle */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-accent" />
          <span className="font-medium text-primary">
            {t("comparison.booksCompared", { count: books.length })}
          </span>
        </div>
        <div className="flex items-center gap-4">
          {/* Show Scores Toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <span className="text-sm text-secondary">{t("comparison.showScores")}</span>
            <button
              onClick={() => setShowScores(!showScores)}
              className={`relative w-10 h-5 rounded-full transition-colors ${
                showScores ? "bg-amber-400" : "bg-secondary/30"
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                  showScores ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
          </label>
          {/* Sort Dropdown */}
          <div className="flex items-center gap-2">
            <ArrowUpDown className="w-4 h-4 text-secondary" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              className="text-sm border border-secondary/20 rounded px-3 py-1.5 bg-white dark:bg-slate-700 text-primary focus:outline-none focus:ring-2 focus:ring-accent/50"
            >
              {sortOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t("comparison.sortBy")} {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Comparison Table */}
      {renderComparisonTable()}

      {/* Detailed Book Sections */}
      <h2 className="text-xl font-semibold text-primary mb-6">{t("bookDetails.title")}</h2>
      {sortedBooks.map((book) => renderBookSection(book))}

      {/* Dimension Legend */}
      <div className="mt-8 p-4 bg-secondary/5 rounded-lg">
        <h3 className="font-medium text-primary mb-3 flex items-center gap-2">
          <Info className="w-4 h-4" />
          {t("dimensions.title")}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {comparisonDimensions.map((dim) => (
            <div key={dim.slug} className="text-sm">
              <div className="font-medium text-primary">{dim.name}</div>
              <div className="text-secondary text-xs">{dim.description}</div>
              <div className="text-accent text-xs mt-1">
                {t("dimensions.evaluatedBy")} {dim.howEvaluated}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Affiliate Disclaimer - At bottom */}
      <div className="mt-8 p-3 bg-secondary/5 border border-secondary/20 rounded-lg">
        <p className="text-xs text-secondary">
          {t("footer.affiliateDisclaimer")}
        </p>
      </div>

      {/* Footer Note */}
      <div className="mt-6 text-sm text-secondary text-center">
        <p>
          {t("footer.encourageExplore")}
        </p>
        <p className="mt-2">
          {t("footer.dataUpdated")} •{" "}
          <a
            href="https://github.com/victordibia/designing-multiagent-systems/issues/new"
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent hover:underline"
          >
            {t("footer.reportIssue")}
          </a>
        </p>
      </div>
    </div>
  );
};

export default BookComparisonLab;
